import Papa from "papaparse";
import * as XLSX from "xlsx";

const asTwoDecimals = (value) => {
    const parsed = Number.parseFloat(value);
    if (Number.isNaN(parsed)) {
        return 0;
    }
    return Number(parsed.toFixed(2));
};

const normalizeKey = (key) => (key || "").toString().trim().toLowerCase();

const getHeaderIndexMap = (headerRow = []) => {
    const indexMap = {};
    headerRow.forEach((key, index) => {
        indexMap[normalizeKey(key)] = index;
    });
    return indexMap;
};

const readCell = (row, indexMap, key) => {
    const idx = indexMap[normalizeKey(key)];
    if (idx === undefined) {
        return undefined;
    }
    return row[idx];
};

const readCellAny = (row, indexMap, keys = []) => {
    for (const key of keys) {
        const value = readCell(row, indexMap, key);
        if (value !== undefined && value !== null && String(value).trim() !== "") {
            return value;
        }
    }
    return undefined;
};

const findHeaderRowIndex = (rows, headerTokens = []) => {
    const tokens = new Set(headerTokens.map(normalizeKey));
    const limit = Math.min(rows.length, 15);
    for (let i = 0; i < limit; i += 1) {
        const cells = (rows[i] || []).map(normalizeKey);
        const matched = cells.filter((cell) => tokens.has(cell)).length;
        if (matched >= 2) {
            return i;
        }
    }
    return 0;
};

export const parseCustomTransactionsCsv = (csvText) => {
    const parsed = Papa.parse(csvText, { header: true });
    return parsed.data.filter((transaction) => transaction.tracking_id !== "");
};

export const parseShipbluTransactionsCsv = (csvText) => {
    const parsed = Papa.parse(csvText, { header: true });
    const transactions = [];
    const filteredTransactions = parsed.data.filter((transaction) =>
        transaction[" Invoice Number"] !== "" && transaction[" Invoice Number"] !== " "
    );

    filteredTransactions.forEach((transaction) => {
        transactions.push({
            order_id: transaction[" Reference Number"],
            cod: transaction[" Status"] === "delivered" ? asTwoDecimals(transaction["Cash Amount"]) : 0,
            cashoutId: transaction[" Invoice Number"],
        });

        transactions.push({
            order_id: transaction[" Reference Number"],
            fees: asTwoDecimals(transaction["Total Fees"]) + asTwoDecimals(transaction[" VAT"]),
            service: transaction[" Status"] === "delivered" ? "DLV" : transaction[" Status"] === "returned" ? "RTN" : "NONE",
            cashoutId: transaction[" Invoice Number"],
        });
    });

    return transactions;
};

export const parseBostaTransactionsSheet = (arrayBuffer) => {
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
    const header = parsedData[0] || [];
    const transactions = [];

    parsedData.slice(1).forEach((row) => {
        const transaction = {};
        row.forEach((value, index) => {
            transaction[header[index]] = value;
        });

        transactions.push({
            tracking_id: transaction["Order Id"] || transaction["Order ID"],
            ...((transaction["COD"] || transaction["Online Payment Amount"])
                ? { cod: asTwoDecimals(transaction["COD"]) + asTwoDecimals(transaction["Online Payment Amount"]) }
                : {}),
            ...(transaction["Compensation Amount"]
                ? { compensation: asTwoDecimals(transaction["Compensation Amount"]), service: "Compensation" }
                : {}),
            cashoutId: transaction["Cash-out ID"],
        });

        if (!transaction["Compensation Amount"]) {
            transactions.push({
                tracking_id: transaction["Order Id"] || transaction["Order ID"],
                fees: asTwoDecimals(transaction["Total Fees"]),
                service: transaction["Order Status"] === "DELIVERED" ? "DLV" : transaction["Order Status"] === "RETURNED" ? "RTN" : "NONE",
                cashoutId: transaction["Cash-out ID"],
            });
        }
    });

    return transactions;
};

const mapFincartService = (status, subStatus) => {
    const normalizedStatus = normalizeKey(status);
    const normalizedSubStatus = normalizeKey(subStatus);

    // Invoice sheets may already use DLV / RTN.
    if (normalizedStatus === "rtn" || normalizedStatus === "rto") {
        return "RTN";
    }
    if (normalizedStatus === "dlv") {
        return "DLV";
    }

    if (
        normalizedStatus === "unsuccessful"
        || normalizedStatus.includes("return")
        || normalizedSubStatus.includes("return")
        || normalizedSubStatus.includes("failed")
    ) {
        return "RTN";
    }

    if (
        normalizedStatus === "successful"
        || normalizedStatus === "delivered"
        || normalizedSubStatus.includes("delivered")
    ) {
        return "DLV";
    }

    return "NONE";
};

/**
 * Parse a Fincart invoice workbook in the browser.
 * API import is 1:1 on each object — when a row has both amount and shipping_fees,
 * emit two transactions (COD + fees). Do not upload the raw workbook.
 *
 * Column mapping:
 * - tracking / tracking_number → tracking_id
 * - orderNumber → fincart_order_number
 * - reference_number → order_id (Chainz Order ObjectId)
 * - invoice name/id (or file basename) → cashoutId
 * - amount → cod (own row; DLV only)
 * - shipping_fees → fees (own row; service from status)
 * - status DLV/RTN (or successful/unsuccessful) → service
 */
export const parseFincartTransactionsSheet = (arrayBuffer, fallbackCashoutId = "FINCART") => {
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: "array" });
    const firstSheetName = workbook.SheetNames[0];
    const worksheet = workbook.Sheets[firstSheetName];
    const parsedData = XLSX.utils.sheet_to_json(worksheet, { header: 1, raw: false });
    const headerRowIndex = findHeaderRowIndex(parsedData, [
        "tracking",
        "tracking_number",
        "trackingnumber",
        "ordernumber",
        "order_number",
        "order number",
        "reference_number",
        "referencenumber",
        "refnumber",
        "ref_number",
        "reference number",
        "amount",
        "shipping_fees",
        "shipping fees",
        "shippingfees",
        "status",
    ]);
    const header = parsedData[headerRowIndex] || [];
    const indexMap = getHeaderIndexMap(header);
    const transactions = [];

    parsedData.slice(headerRowIndex + 1).forEach((row) => {
        const trackingNumber = readCellAny(row, indexMap, [
            "tracking",
            "tracking_number",
            "trackingNumber",
            "Tracking",
        ]);
        const orderNumber = readCellAny(row, indexMap, [
            "orderNumber",
            "order_number",
            "Order Number",
            "order number",
        ]);
        const referenceNumber = readCellAny(row, indexMap, [
            "reference_number",
            "referenceNumber",
            "refNumber",
            "ref_number",
            "Reference Number",
        ]);
        const amount = asTwoDecimals(readCellAny(row, indexMap, ["amount", "Amount", "COD", "cod"]));
        const shippingFees = asTwoDecimals(
            readCellAny(row, indexMap, [
                "shipping_fees",
                "shipping fees",
                "Shipping Fees",
                "shippingFees",
                "fees",
            ])
        );
        const status = readCellAny(row, indexMap, ["status", "Status", "service"]);
        const subStatus = readCellAny(row, indexMap, ["sub_status", "subStatus", "sub status"]);
        // Invoice filename / invoice id is the cashout cycle; reference_number is the Chainz order id.
        const invoiceCashoutId = readCellAny(row, indexMap, [
            "invoice",
            "invoice_id",
            "invoiceId",
            "invoice name",
            "invoice_name",
            "cashoutId",
            "cashout_id",
            "Cash-out ID",
        ]);
        const cashoutId = invoiceCashoutId || fallbackCashoutId;
        const service = mapFincartService(status, subStatus);

        if (!trackingNumber && !orderNumber && !referenceNumber) {
            return;
        }

        // Skip summary / total rows (e.g. "Total Cash Collected")
        if (orderNumber && !trackingNumber && !referenceNumber && Number.isNaN(Number(orderNumber))) {
            const label = normalizeKey(orderNumber);
            if (label.includes("total") || label.includes("insurance") || label.includes("net")) {
                return;
            }
        }

        const base = {
            ...(trackingNumber ? { tracking_id: String(trackingNumber).trim() } : {}),
            ...(orderNumber ? { fincart_order_number: String(orderNumber).trim() } : {}),
            ...(referenceNumber ? { order_id: String(referenceNumber).trim() } : {}),
            cashoutId: String(cashoutId).trim(),
            currency: "EGP",
        };

        // Split amount + shipping_fees into separate rows — API prefers fees when both are on one object.
        if (amount > 0 && service === "DLV") {
            transactions.push({
                ...base,
                cod: amount,
            });
        }

        if (shippingFees > 0) {
            transactions.push({
                ...base,
                fees: shippingFees,
                service,
            });
        }
    });

    return transactions;
};

const readSummaryValue = (rows, label) => {
    const wanted = normalizeKey(label);
    for (const row of rows) {
        if (normalizeKey(row?.[0]) === wanted) {
            const value = row[1];
            return value === undefined || value === null ? "" : String(value).trim();
        }
    }
    return "";
};

// Delivered and exchanged rows collected cash. Returned / RTO rows still list the original COD, but it was not collected.
const mapBlinkService = (status, bucket, type) => {
    const normalizedStatus = normalizeKey(status);
    const normalizedBucket = normalizeKey(bucket);
    const normalizedType = normalizeKey(type);

    if (
        normalizedStatus === "returned"
        || normalizedStatus.includes("return")
        || normalizedBucket === "rto"
        || normalizedBucket === "return"
        || normalizedType === "return"
    ) {
        return "RTN";
    }

    if (
        normalizedStatus === "delivered"
        || normalizedStatus === "exchanged"
        || normalizedBucket === "delivery"
        || normalizedBucket === "exchange"
        || normalizedType === "delivery"
        || normalizedType === "exchange"
    ) {
        return "DLV";
    }

    return "NONE";
};

export const parseBlinkTransactionsSheet = (arrayBuffer, fallbackCashoutId = "BLINK") => {
    const data = new Uint8Array(arrayBuffer);
    const workbook = XLSX.read(data, { type: "array" });
    const ordersSheetName = workbook.SheetNames.find((name) => normalizeKey(name) === "payout orders")
        || workbook.SheetNames[0];
    const summarySheetName = workbook.SheetNames.find((name) => normalizeKey(name) === "summary");
    const ordersSheet = workbook.Sheets[ordersSheetName];
    const parsedData = XLSX.utils.sheet_to_json(ordersSheet, { header: 1, raw: false });
    const header = parsedData[0] || [];
    const indexMap = getHeaderIndexMap(header);
    const transactions = [];

    let cashoutId = fallbackCashoutId;
    if (summarySheetName) {
        const summaryRows = XLSX.utils.sheet_to_json(workbook.Sheets[summarySheetName], { header: 1, raw: false });
        const payoutId = readSummaryValue(summaryRows, "Payout ID");
        if (payoutId) {
            cashoutId = payoutId;
        }
    }

    parsedData.slice(1).forEach((row) => {
        const trackingNumber = readCell(row, indexMap, "Tracking");
        const orderCode = readCell(row, indexMap, "Order code");
        const merchantRef = readCell(row, indexMap, "Merchant ref");
        const type = readCell(row, indexMap, "Type");
        const status = readCell(row, indexMap, "Status");
        const bucket = readCell(row, indexMap, "Bucket");
        // Ledger COD = Cash collected. Never use COD (intended) — RTOs keep intended set with 0 collected.
        const cashCollected = readCell(row, indexMap, "Cash collected")
            ?? readCell(row, indexMap, "Cash Collected")
            ?? readCell(row, indexMap, "cash_collected");
        const legacyCod = cashCollected === undefined
            ? readCell(row, indexMap, "COD")
            : undefined;
        const cod = asTwoDecimals(cashCollected ?? legacyCod);
        const finalFee = asTwoDecimals(
            readCell(row, indexMap, "Final fee")
            ?? readCell(row, indexMap, "Final Fee")
        );
        const service = mapBlinkService(status, bucket, type);

        if (!trackingNumber && !orderCode && !merchantRef) {
            return;
        }

        const base = {
            tracking_id: trackingNumber || undefined,
            blink_order_code: orderCode || undefined,
            ...(merchantRef ? { order_id: merchantRef } : {}),
            cashoutId,
            currency: "EGP",
            source: "Blink",
        };

        if (cod > 0 && service === "DLV") {
            transactions.push({
                ...base,
                cod,
            });
        }

        if (finalFee > 0) {
            transactions.push({
                ...base,
                fees: finalFee,
                service,
            });
        }
    });

    return transactions;
};
